import React from 'react';
import { Folder, PanelLeft, Search, SquarePen } from 'lucide-react';
export const toolLogo = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;

/**
 * Shared chrome for every real-product screenshot on the page. One frame means
 * Connections, the platform primitives and the brain all read as the same
 * application rather than four unrelated illustrations.
 */
export function AppChrome({
  children,
  sidebar = true,
  active,
  className = ''
}: {
  children: React.ReactNode;
  sidebar?: boolean;
  active?: string;
  className?: string;
}) {
  return <div className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-189" data-mp="2q1vltd22wepw">
      <div className="flex items-center gap-2 border-b border-[#EFEAE1] bg-[#FBF8F3] px-4 py-2.5" data-id="element-190" data-mp="2uuezea0rn9up">
        <span className="flex size-[18px] items-center justify-center rounded-md bg-[#EA580C]" data-id="element-191" data-mp="1xbn9i1u84oj8">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png" alt="" className="size-3 [filter:brightness(0)_invert(1)]" data-id="element-192" data-mp="22azs2ae2b9g0" />
        </span>
        <span className="font-title text-[13px] font-semibold tracking-tight text-[#2B211A]" data-id="element-193" data-mp="1cb76y56nmua">
          Giga
        </span>
        <span className="ml-1 truncate rounded-md bg-white px-2 py-0.5 font-mono text-[10.5px] text-neutral-400 ring-1 ring-[#EFEAE1]" data-id="element-194" data-mp="3si2xpymvh1hm">
          app.getgiga.com
        </span>
      </div>
      <div className="flex min-h-[420px]" data-id="element-195" data-mp="3ikopgmb3rj0e">
        {sidebar && <AppSidebar active={active} data-id="element-196" data-mp="3inayyrhk08l9" />}
        <div className="min-w-0 flex-1 bg-white" data-id="element-197" data-mp="3eil2b0f2mpza">{children}</div>
      </div>
    </div>;
}
const SIDEBAR_TRACKS = ['Onboarding revamp', 'Q3 pipeline', 'Brand refresh', 'Renewals'];
const SIDEBAR_CHATS = ['Inbox catch-up', 'Renewal risk', 'Board deck notes'];
function AppSidebar({
  active
}: {
  active?: string;
}) {
  return <aside className="hidden w-[196px] shrink-0 flex-col border-r border-[#EFEAE1] bg-[#FCFAF6] px-2.5 py-3 lg:flex" data-id="element-198" data-mp="17u83eytj35sh">
      <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-199" data-mp="1qw8mh6ofkt9j">
        <span className="flex items-center gap-2" data-id="element-200" data-mp="1vpa0zdkq6qsl">
          <span className="flex size-6 items-center justify-center rounded-lg bg-[#134E4A] text-[10px] font-bold text-white" data-id="element-201" data-mp="1hwtws9g38mq3">
            BW
          </span>
          <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-202" data-mp="1mwg9mhs080yd">
            Brightwave
          </span>
        </span>
        <PanelLeft size={13} className="text-neutral-300" data-id="element-203" data-mp="2ajel1119oipf" />
      </div>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#E7E0D4] bg-white px-2.5 py-1.5" data-id="element-204" data-mp="2elt33ix43efn">
        <SquarePen size={12} className="text-neutral-400" data-id="element-205" data-mp="20n39id9i3ww1" />
        <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-206" data-mp="25lmxej7nnir1">
          New chat
        </span>
      </div>
      <div className="mb-1 flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 ring-1 ring-[#EFEAE1]" data-id="element-207" data-mp="p74rvl09c2j7">
        <Search size={11} className="text-neutral-300" data-id="element-208" data-mp="u829nwhcuuk6" />
        <span className="text-[11.5px] text-neutral-400" data-id="element-209" data-mp="mnhyua2vm076">Search</span>
      </div>
      <SidebarGroup label="Manage" items={['Assistants', 'Pulses', 'Connections', 'Groups']} active={active} data-id="element-210" data-mp="mpky7kpx55ok" />
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-211" data-mp="vog6r3gfqrv1">
        Tracks
      </p>
      <ul data-id="element-212" data-mp="qp3eg36sosl2">
        {SIDEBAR_TRACKS.map(t => <li key={t} className="flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-213" data-mp="qq4r2c3gln4w">
            <Folder size={11} strokeWidth={1.9} className="shrink-0 text-neutral-300" data-id="element-214" data-mp="3v3jtek52gn10" />
            <span className="truncate" data-id="element-215" data-mp="3v1qi2rjidemo">{t}</span>
          </li>)}
      </ul>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-216" data-mp="cr53z8vlhgau">
        Chats
      </p>
      <ul data-id="element-217" data-mp="7x0vumalf286">
        {SIDEBAR_CHATS.map(c => <li key={c} className="truncate rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-218" data-mp="3gdvo77trl480">
            {c}
          </li>)}
      </ul>
    </aside>;
}
function SidebarGroup({
  label,
  items,
  active
}: {
  label: string;
  items: string[];
  active?: string;
}) {
  return <>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-219" data-mp="3u0l06zw7rm17">
        {label}
      </p>
      <ul data-id="element-220" data-mp="2ti9sao79js1">
        {items.map(item => <li key={item} className={`rounded-lg px-2 py-[5px] text-[12px] ${active === item ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-500'}`} data-id="element-221" data-mp="2t8f49s7mog0">
            {item}
          </li>)}
      </ul>
    </>;
}